import React from "react";

import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useMaterialSubcategoryApi from "hooks/api/private/useMaterialSubcategoryApi";

import { LanguageEnum } from "types/api/commons";
import { MaterialSubcategoryBody, MaterialSubcategoryDetails } from "types/api/materialSubcategory";

export type MaterialSubcategoryBodyForm = {
    text: {
        [x in LanguageEnum]: {
            name: string;
        };
    };
};

export type MaterialSubcategoryFormHook = {
    formHook: FormHookType<MaterialSubcategoryBodyForm>;
};

function useMaterialSubcategoryForm({
    materialSubcategoryId,
}: {
    materialSubcategoryId?: string;
}): MaterialSubcategoryFormHook {
    const { createMaterialSubcategory, updateMaterialSubcategory, getOneMaterialSubcategory } =
        useMaterialSubcategoryApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            name: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: MaterialSubcategoryBodyForm = React.useMemo(() => {
        return {
            text: {
                it: { name: "" },
                en: { name: "" },
            },
        };
    }, []);

    const formHook = useForm<MaterialSubcategoryBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [details, setDetails] = React.useState<MaterialSubcategoryDetails | undefined>(undefined);

    React.useEffect(() => {
        if (materialSubcategoryId) {
            const [apiCall, abortController] = getOneMaterialSubcategory({
                id: materialSubcategoryId,
            });

            apiCall.then(setDetails);

            return () => abortController.abort();
        }
    }, [getOneMaterialSubcategory, materialSubcategoryId]);

    React.useEffect(() => {
        if (details) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.text.it.name = details.text.it.name;
                newForm.text.en.name = details.text.en.name;

                return newForm;
            });
        }
    }, [setForm, details]);

    const body: MaterialSubcategoryBody = React.useMemo(() => {
        return {
            text: {
                it: { name: form.text.it.name },
                en: { name: form.text.en.name },
            },
        };
    }, [form]);

    React.useEffect(() => {
        addSaveAction("useMaterialSubcategoryForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                if (materialSubcategoryId) {
                    const [apiCall] = updateMaterialSubcategory(
                        { id: materialSubcategoryId },
                        { ...body, id: materialSubcategoryId },
                    );
                    promises.push(apiCall);
                } else {
                    const [apiCall] = createMaterialSubcategory({
                        ...body,
                        id: uuidv4(),
                    });
                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createMaterialSubcategory,
        updateMaterialSubcategory,
        body,
        materialSubcategoryId,
        isValid,
    ]);

    return React.useMemo(
        (): MaterialSubcategoryFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default useMaterialSubcategoryForm;
